import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config';

const AIInterviewDashboard = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadSubmissions();
    loadStats();
  }, []);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE_URL}/api/ai-interview/admin/all-submissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) setSubmissions(response.data.data);
    } catch (error) {
      console.error('Error loading submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = sessionStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE_URL}/api/ai-interview/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) setStats(response.data.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const getStatusBadge = (score) => {
    if (score >= 75) return { color: '#059669', bg: '#ECFDF5', text: 'Excellent', dot: '#10B981' };
    if (score >= 60) return { color: '#2563EB', bg: '#EFF6FF', text: 'Passed', dot: '#3B82F6' };
    if (score >= 40) return { color: '#D97706', bg: '#FFFBEB', text: 'Needs Work', dot: '#F59E0B' };
    return { color: '#DC2626', bg: '#FEF2F2', text: 'Failed', dot: '#EF4444' };
  };

  // ✅ Build video URL from filename
  const getVideoUrl = (submission) => {
    // Check communicationVideo field
    if (submission.communicationVideo?.videoFilename) {
      return `${API_BASE_URL}/api/ai-interview/video/${submission.communicationVideo.videoFilename}`;
    }
    // Check answers array for communication question
    const commAnswer = submission.answers?.find(a => a.isCommunicationQuestion === true);
    if (commAnswer?.videoRecording?.videoFilename) {
      return `${API_BASE_URL}/api/ai-interview/video/${commAnswer.videoRecording.videoFilename}`;
    }
    return null;
  };

  const hasVideo = (submission) => {
    return !!(submission.communicationVideo?.videoFilename || 
      submission.answers?.find(a => a.isCommunicationQuestion === true)?.videoRecording?.videoFilename);
  };

  const filteredSubmissions = submissions.filter(sub => {
    const score = sub.evaluation?.averageScore || 0;
    if (filter === 'passed' && score < 60) return false;
    if (filter === 'failed' && score >= 60) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        sub.candidateName?.toLowerCase().includes(term) ||
        sub.candidateEmail?.toLowerCase().includes(term) ||
        sub.position?.toLowerCase().includes(term)
      );
    }
    return true;
  });

  const passedCount = submissions.filter(s => (s.evaluation?.averageScore || 0) >= 60).length;
  const failedCount = submissions.filter(s => (s.evaluation?.averageScore || 0) < 60).length;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

        .aid-root {
          font-family: 'Outfit', sans-serif;
          background: #F4F6FB;
          min-height: 100vh;
          padding: 32px 36px;
          margin-left: 280px;
          box-sizing: border-box;
        }

        .aid-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 32px;
        }
        .aid-header-title {
          font-size: 26px;
          font-weight: 700;
          color: #0F172A;
          letter-spacing: -0.5px;
          margin: 0 0 4px;
        }
        .aid-header-sub {
          font-size: 13.5px;
          color: #64748B;
          margin: 0;
        }
        .aid-refresh-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 9px 18px;
          background: #fff;
          border: 1px solid #E2E8F0;
          border-radius: 10px;
          font-size: 13.5px;
          font-weight: 500;
          color: #374151;
          cursor: pointer;
          transition: background 0.15s, box-shadow 0.15s;
        }
        .aid-refresh-btn:hover {
          background: #F8FAFC;
          box-shadow: 0 2px 8px rgba(0,0,0,0.07);
        }

        .aid-stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
          margin-bottom: 28px;
        }
        @media (max-width: 1200px) {
          .aid-stats-grid { grid-template-columns: repeat(2, 1fr); }
        }
        .aid-stat-card {
          background: #fff;
          border-radius: 14px;
          padding: 20px 22px;
          border: 1px solid #E8EDF5;
          display: flex;
          align-items: center;
          gap: 16px;
        }
        .aid-stat-icon-wrap {
          width: 46px;
          height: 46px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          font-size: 20px;
        }
        .aid-stat-icon-wrap.indigo { background: #EEF2FF; }
        .aid-stat-icon-wrap.emerald { background: #ECFDF5; }
        .aid-stat-icon-wrap.rose { background: #FFF1F2; }
        .aid-stat-icon-wrap.amber { background: #FFFBEB; }
        .aid-stat-num {
          font-size: 28px;
          font-weight: 700;
          color: #0F172A;
          line-height: 1;
        }
        .aid-stat-label {
          font-size: 12.5px;
          color: #94A3B8;
          margin-top: 4px;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.4px;
        }

        .aid-filter-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          gap: 16px;
          flex-wrap: wrap;
        }
        .aid-filter-tabs {
          display: flex;
          background: #fff;
          border: 1px solid #E2E8F0;
          border-radius: 10px;
          padding: 4px;
          gap: 2px;
        }
        .aid-tab {
          padding: 7px 16px;
          border-radius: 7px;
          border: none;
          background: transparent;
          font-size: 13px;
          font-weight: 500;
          color: #64748B;
          cursor: pointer;
          transition: all 0.15s;
        }
        .aid-tab.active {
          background: #1E3A8A;
          color: #fff;
        }
        .aid-search-wrap {
          position: relative;
          flex: 1;
          max-width: 340px;
          min-width: 200px;
        }
        .aid-search-input {
          width: 100%;
          box-sizing: border-box;
          padding: 9px 14px 9px 36px;
          border: 1px solid #E2E8F0;
          border-radius: 10px;
          font-size: 13.5px;
          font-family: 'Outfit', sans-serif;
          color: #0F172A;
          background: #fff;
          outline: none;
          transition: border-color 0.15s;
        }
        .aid-search-input:focus {
          border-color: #3B82F6;
          box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
        }

        .aid-list { display: flex; flex-direction: column; gap: 12px; }

        .aid-card {
          background: #fff;
          border: 1px solid #E8EDF5;
          border-radius: 14px;
          padding: 20px 22px;
          cursor: pointer;
          transition: box-shadow 0.18s, transform 0.18s;
        }
        .aid-card:hover {
          box-shadow: 0 6px 24px rgba(15,23,42,0.08);
          transform: translateY(-1px);
          border-color: #C7D2FE;
        }

        .aid-card-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          gap: 12px;
        }
        .aid-candidate-row {
          display: flex;
          align-items: center;
          gap: 13px;
        }
        .aid-avatar {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: linear-gradient(135deg, #1E3A8A 0%, #3B82F6 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 17px;
          font-weight: 700;
          color: #fff;
        }
        .aid-name { font-size: 15px; font-weight: 600; color: #0F172A; }
        .aid-email { font-size: 12.5px; color: #94A3B8; margin-top: 2px; }

        .aid-score-col { display: flex; align-items: center; gap: 12px; flex-shrink: 0; }
        .aid-score-ring {
          width: 54px;
          height: 54px;
          border-radius: 50%;
          border: 2.5px solid;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 15px;
          font-weight: 700;
          font-family: 'JetBrains Mono', monospace;
        }
        .aid-status-pill {
          padding: 4px 11px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 5px;
        }
        .aid-status-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
        }

        .aid-meta-row {
          display: flex;
          gap: 20px;
          flex-wrap: wrap;
          padding: 12px 0;
          border-top: 1px solid #F1F5F9;
          border-bottom: 1px solid #F1F5F9;
          margin-bottom: 14px;
        }
        .aid-meta-item {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12.5px;
          color: #64748B;
        }
        .aid-video-badge {
          background: #FEF3C7;
          color: #D97706;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 10px;
          font-weight: 600;
          margin-left: 8px;
        }

        .aid-progress-track {
          height: 5px;
          background: #F1F5F9;
          border-radius: 99px;
          overflow: hidden;
          margin-bottom: 14px;
        }
        .aid-progress-fill {
          height: 100%;
          border-radius: 99px;
        }

        .aid-details-btn {
          font-size: 12.5px;
          font-weight: 600;
          color: #2563EB;
          background: #EFF6FF;
          border: none;
          border-radius: 7px;
          padding: 6px 14px;
          cursor: pointer;
          transition: background 0.15s;
        }
        .aid-details-btn:hover { background: #DBEAFE; }

        .aid-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 80px 20px;
          gap: 16px;
          color: #94A3B8;
        }
        .aid-spinner {
          width: 36px; height: 36px;
          border: 3px solid #E2E8F0;
          border-top-color: #2563EB;
          border-radius: 50%;
          animation: aidSpin 0.75s linear infinite;
        }
        @keyframes aidSpin { to { transform: rotate(360deg); } }

        .aid-empty {
          text-align: center;
          padding: 72px 20px;
          background: #fff;
          border-radius: 14px;
          color: #94A3B8;
        }
        .aid-empty-icon { font-size: 40px; margin-bottom: 14px; }

        /* Modal Styles */
        .aid-overlay {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.55);
          backdrop-filter: blur(3px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 99999;
          padding: 24px;
        }

        .aid-modal {
          background: #fff;
          border-radius: 18px;
          width: 100%;
          max-width: 900px;
          max-height: 88vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          box-shadow: 0 24px 60px rgba(15,23,42,0.2);
        }

        .aid-mhdr {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 26px;
          border-bottom: 1px solid #F1F5F9;
          flex-shrink: 0;
        }
        .aid-mhdr-left { display: flex; align-items: center; gap: 12px; }
        .aid-mhdr-avatar {
          width: 40px; height: 40px;
          border-radius: 50%;
          background: linear-gradient(135deg, #1E3A8A, #3B82F6);
          display: flex; align-items: center; justify-content: center;
          font-size: 16px; font-weight: 700; color: #fff;
        }
        .aid-mhdr-name { font-size: 16px; font-weight: 700; color: #0F172A; margin: 0; }
        .aid-mhdr-pos { font-size: 12.5px; color: #64748B; margin: 2px 0 0; }
        .aid-mclose {
          width: 34px; height: 34px;
          border-radius: 8px;
          border: 1px solid #E2E8F0;
          background: none;
          cursor: pointer;
          font-size: 18px;
          color: #64748B;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.15s;
        }
        .aid-mclose:hover { background: #F8FAFC; }

        .aid-mbody {
          flex: 1;
          overflow-y: auto;
          padding: 24px 26px;
        }
        .aid-mbody::-webkit-scrollbar { width: 6px; }
        .aid-mbody::-webkit-scrollbar-track { background: transparent; }
        .aid-mbody::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 3px; }

        .aid-msec { margin-bottom: 26px; }
        .aid-msec-title {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.8px;
          color: #94A3B8;
          margin: 0 0 12px;
        }

        .aid-info-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 10px;
        }
        @media (max-width: 640px) {
          .aid-info-grid { grid-template-columns: 1fr; }
        }
        .aid-info-cell {
          background: #F8FAFC;
          border-radius: 10px;
          padding: 12px 14px;
          border: 1px solid #F1F5F9;
        }
        .aid-info-label {
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #94A3B8;
          margin-bottom: 4px;
        }
        .aid-info-value {
          font-size: 13.5px;
          color: #0F172A;
          font-weight: 500;
        }

        .aid-score-strip {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
        }
        .aid-score-tile {
          background: #F8FAFC;
          border: 1px solid #F1F5F9;
          border-radius: 12px;
          padding: 16px;
          text-align: center;
        }
        .aid-score-tile-label { font-size: 11px; color: #94A3B8; font-weight: 600; text-transform: uppercase; margin-bottom: 8px; }
        .aid-score-tile-val { font-size: 28px; font-weight: 700; font-family: 'JetBrains Mono', monospace; color: #0F172A; line-height: 1; }
        .aid-score-tile-sub { font-size: 12px; margin-top: 6px; font-weight: 600; }

        /* Video Player Styles */
        .aid-video-section {
          background: #F8FAFC;
          border-radius: 12px;
          padding: 16px;
          margin-top: 8px;
          border: 1px solid #F1F5F9;
        }
        .aid-video-title {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 12px;
          font-size: 14px;
          font-weight: 600;
          color: #0F172A;
        }
        .aid-video-player {
          width: 100%;
          border-radius: 10px;
          background: #000;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        .aid-video-info {
          margin-top: 10px;
          font-size: 12px;
          color: #64748B;
          display: flex;
          justify-content: space-between;
        }

        .aid-qa-item {
          border: 1px solid #F1F5F9;
          border-radius: 12px;
          overflow: hidden;
          margin-bottom: 12px;
        }
        .aid-qa-header {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          background: #F8FAFC;
          border-bottom: 1px solid #F1F5F9;
          flex-wrap: wrap;
        }
        .aid-qnum {
          background: #1E3A8A;
          color: #fff;
          border-radius: 6px;
          padding: 3px 10px;
          font-size: 11.5px;
          font-weight: 700;
          font-family: 'JetBrains Mono', monospace;
        }
        .aid-qscore {
          font-size: 12.5px;
          font-weight: 700;
          font-family: 'JetBrains Mono', monospace;
        }
        .aid-qpass {
          font-size: 11.5px;
          font-weight: 700;
          padding: 3px 10px;
          border-radius: 20px;
        }
        .aid-qa-body { padding: 16px; }
        .aid-question-text {
          font-size: 14px;
          font-weight: 600;
          color: #0F172A;
          line-height: 1.55;
          margin-bottom: 12px;
        }
        .aid-ans-label {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #94A3B8;
          margin-bottom: 6px;
        }
        .aid-ans-text {
          font-size: 13.5px;
          color: #334155;
          line-height: 1.65;
          background: #F8FAFC;
          border-radius: 8px;
          padding: 12px 14px;
          border: 1px solid #F1F5F9;
        }
        .aid-kw-wrap {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-top: 12px;
        }
        .aid-kw-tag {
          font-size: 11.5px;
          font-weight: 600;
          padding: 3px 10px;
          border-radius: 20px;
          background: #EFF6FF;
          color: #2563EB;
          border: 1px solid #BFDBFE;
        }

        .aid-mfooter {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          padding: 16px 26px;
          border-top: 1px solid #F1F5F9;
          flex-shrink: 0;
          gap: 10px;
        }
        .aid-btn-ghost {
          padding: 9px 22px;
          background: transparent;
          border: 1px solid #E2E8F0;
          border-radius: 9px;
          font-size: 13.5px;
          font-weight: 600;
          color: #64748B;
          cursor: pointer;
        }
        .aid-btn-primary {
          padding: 9px 22px;
          background: #1E3A8A;
          border: none;
          border-radius: 9px;
          font-size: 13.5px;
          font-weight: 600;
          color: #fff;
          cursor: pointer;
        }

        .aid-divider { height: 1px; background: #F1F5F9; margin: 4px 0 20px; }
        .video-loading { text-align: center; padding: 20px; color: #64748B; }
        .video-error { text-align: center; padding: 20px; color: #DC2626; }
      `}</style>

      <div className="aid-root">
        <div className="aid-header">
          <div>
            <h1 className="aid-header-title">AI Interview Dashboard</h1>
            <p className="aid-header-sub">Review candidate responses and AI-powered evaluations</p>
          </div>
          <button className="aid-refresh-btn" onClick={() => { loadSubmissions(); loadStats(); }}>
            ↺ Refresh
          </button>
        </div>

        {stats && (
          <div className="aid-stats-grid">
            <div className="aid-stat-card">
              <div className="aid-stat-icon-wrap indigo">📊</div>
              <div>
                <div className="aid-stat-num">{stats.totalSubmissions}</div>
                <div className="aid-stat-label">Total Submissions</div>
              </div>
            </div>
            <div className="aid-stat-card">
              <div className="aid-stat-icon-wrap emerald">✅</div>
              <div>
                <div className="aid-stat-num">{stats.passedCount}</div>
                <div className="aid-stat-label">Passed</div>
              </div>
            </div>
            <div className="aid-stat-card">
              <div className="aid-stat-icon-wrap rose">❌</div>
              <div>
                <div className="aid-stat-num">{stats.failedCount}</div>
                <div className="aid-stat-label">Failed</div>
              </div>
            </div>
            <div className="aid-stat-card">
              <div className="aid-stat-icon-wrap amber">⭐</div>
              <div>
                <div className="aid-stat-num">{stats.averageScore}%</div>
                <div className="aid-stat-label">Average Score</div>
              </div>
            </div>
          </div>
        )}

        <div className="aid-filter-bar">
          <div className="aid-filter-tabs">
            <button className={`aid-tab ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
              All ({submissions.length})
            </button>
            <button className={`aid-tab ${filter === 'passed' ? 'active' : ''}`} onClick={() => setFilter('passed')}>
              Passed ({passedCount})
            </button>
            <button className={`aid-tab ${filter === 'failed' ? 'active' : ''}`} onClick={() => setFilter('failed')}>
              Failed ({failedCount})
            </button>
          </div>
          <div className="aid-search-wrap">
            <input
              className="aid-search-input"
              type="text"
              placeholder="🔍 Search by name, email or position…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <div className="aid-loading">
            <div className="aid-spinner" />
            <span>Loading submissions…</span>
          </div>
        ) : filteredSubmissions.length === 0 ? (
          <div className="aid-empty">
            <div className="aid-empty-icon">📭</div>
            <p>No AI Interview submissions found</p>
          </div>
        ) : (
          <div className="aid-list">
            {filteredSubmissions.map(submission => {
              const score = submission.evaluation?.averageScore || 0;
              const status = getStatusBadge(score);
              const dateStr = new Date(submission.submittedAt || submission.completedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
              const mins = Math.floor((submission.timeSpent || 0) / 60);
              const hasVideoSubmission = hasVideo(submission);

              return (
                <div key={submission._id} className="aid-card" onClick={() => setSelectedSubmission(submission)}>
                  <div className="aid-card-top">
                    <div className="aid-candidate-row">
                      <div className="aid-avatar">{submission.candidateName?.charAt(0)?.toUpperCase() || '?'}</div>
                      <div>
                        <div className="aid-name">{submission.candidateName}</div>
                        <div className="aid-email">{submission.candidateEmail}</div>
                      </div>
                    </div>
                    <div className="aid-score-col">
                      <div className="aid-score-ring" style={{ borderColor: status.dot, color: status.dot }}>
                        {score}%
                      </div>
                      <div className="aid-status-pill" style={{ background: status.bg, color: status.color }}>
                        <span className="aid-status-dot" style={{ background: status.dot }} />
                        {status.text}
                      </div>
                    </div>
                  </div>

                  <div className="aid-meta-row">
                    <div className="aid-meta-item">💼 <span>{submission.position}</span></div>
                    <div className="aid-meta-item">📅 <span>{dateStr}</span></div>
                    <div className="aid-meta-item">⏱ <span>{mins} min</span></div>
                    <div className="aid-meta-item">📝 <span>{submission.answers?.length || 0} questions</span></div>
                    {hasVideoSubmission && <div className="aid-meta-item">🎥 <span className="aid-video-badge">Communication Video</span></div>}
                  </div>

                  <div className="aid-progress-track">
                    <div className="aid-progress-fill" style={{ width: `${score}%`, background: status.dot }} />
                  </div>

                  <button className="aid-details-btn">View Details →</button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal */}
      {selectedSubmission && (() => {
        const sub = selectedSubmission;
        const score = sub.evaluation?.averageScore || 0;
        const status = getStatusBadge(score);
        const mins = Math.floor((sub.timeSpent || 0) / 60);
        const secs = (sub.timeSpent || 0) % 60;
        const videoUrl = getVideoUrl(sub);
        const hasVideoSubmission = !!videoUrl;

        return (
          <div className="aid-overlay" onClick={() => setSelectedSubmission(null)}>
            <div className="aid-modal" onClick={e => e.stopPropagation()}>
              <div className="aid-mhdr">
                <div className="aid-mhdr-left">
                  <div className="aid-mhdr-avatar">{sub.candidateName?.charAt(0)?.toUpperCase() || '?'}</div>
                  <div>
                    <p className="aid-mhdr-name">{sub.candidateName}</p>
                    <p className="aid-mhdr-pos">{sub.position} • {sub.candidateEmail}</p>
                  </div>
                </div>
                <button className="aid-mclose" onClick={() => setSelectedSubmission(null)}>×</button>
              </div>

              <div className="aid-mbody">
                {/* Candidate Info */}
                <div className="aid-msec">
                  <p className="aid-msec-title">Candidate Information</p>
                  <div className="aid-info-grid">
                    <div className="aid-info-cell">
                      <div className="aid-info-label">Full Name</div>
                      <div className="aid-info-value">{sub.candidateName}</div>
                    </div>
                    <div className="aid-info-cell">
                      <div className="aid-info-label">Email</div>
                      <div className="aid-info-value">{sub.candidateEmail}</div>
                    </div>
                    <div className="aid-info-cell">
                      <div className="aid-info-label">Applied Position</div>
                      <div className="aid-info-value">{sub.position}</div>
                    </div>
                    <div className="aid-info-cell">
                      <div className="aid-info-label">Submitted At</div>
                      <div className="aid-info-value">
                        {new Date(sub.submittedAt || sub.completedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                    <div className="aid-info-cell">
                      <div className="aid-info-label">Time Spent</div>
                      <div className="aid-info-value">{mins} min {secs} sec</div>
                    </div>
                    <div className="aid-info-cell">
                      <div className="aid-info-label">Questions Answered</div>
                      <div className="aid-info-value">{sub.answers?.length || 0}</div>
                    </div>
                  </div>
                </div>

                <div className="aid-divider" />

                {/* Score Summary */}
                <div className="aid-msec">
                  <p className="aid-msec-title">Evaluation Summary</p>
                  <div className="aid-score-strip">
                    <div className="aid-score-tile">
                      <div className="aid-score-tile-label">Overall Score</div>
                      <div className="aid-score-tile-val" style={{ color: status.dot }}>{score}%</div>
                      <div className="aid-score-tile-sub" style={{ color: status.color, background: status.bg, display: 'inline-block', padding: '3px 12px', borderRadius: '20px', marginTop: '8px' }}>
                        {sub.evaluation?.isPassed ? '✅ Passed' : '❌ Failed'}
                      </div>
                    </div>
                    <div className="aid-score-tile">
                      <div className="aid-score-tile-label">Total Score</div>
                      <div className="aid-score-tile-val">
                        {sub.evaluation?.totalScore || 0}
                        <span style={{ fontSize: '16px', color: '#94A3B8' }}>/{(sub.evaluation?.totalQuestions || 0) * 100}</span>
                      </div>
                    </div>
                    <div className="aid-score-tile">
                      <div className="aid-score-tile-label">Passing Score</div>
                      <div className="aid-score-tile-val">{sub.evaluation?.passingPercentage || 60}%</div>
                    </div>
                  </div>
                </div>

                <div className="aid-divider" />

                {/* ✅ Communication Video Section - USING FILE URL */}
                {hasVideoSubmission && (
                  <>
                    <div className="aid-msec">
                      <p className="aid-msec-title">🎥 Communication Assessment Video</p>
                      <div className="aid-video-section">
                        <div className="aid-video-title">
                          <span>📹 Candidate Introduction Video</span>
                        </div>
                        <video 
                          className="aid-video-player" 
                          controls 
                          src={videoUrl}
                          style={{ width: '100%', maxHeight: '400px' }}
                        >
                          Your browser does not support the video tag.
                        </video>
                        <div className="aid-video-info">
                          <span>Duration: {Math.floor((sub.communicationVideo?.duration || 0) / 60)}:{(sub.communicationVideo?.duration || 0) % 60}</span>
                          <span>File Size: {((sub.communicationVideo?.fileSize || 0) / (1024 * 1024)).toFixed(2)} MB</span>
                        </div>
                      </div>
                    </div>
                    <div className="aid-divider" />
                  </>
                )}

                {/* Q&A Section */}
                <div className="aid-msec">
                  <p className="aid-msec-title">Questions & Answers</p>
                  {sub.answers?.filter(a => !a.isCommunicationQuestion).map((answer, idx) => {
                    const question = sub.questions?.find(q => q.questionId === answer.questionId);
                    const isPassing = answer.evaluation?.isPassing;
                    const qScore = answer.evaluation?.score || 0;

                    return (
                      <div key={idx} className="aid-qa-item">
                        <div className="aid-qa-header">
                          <span className="aid-qnum">Q{idx + 1}</span>
                          <span className="aid-qscore" style={{ color: isPassing ? '#059669' : '#DC2626' }}>
                            {qScore}%
                          </span>
                          <span className="aid-qpass" style={{ background: isPassing ? '#ECFDF5' : '#FEF2F2', color: isPassing ? '#059669' : '#DC2626' }}>
                            {isPassing ? '✅ Pass' : '❌ Fail'}
                          </span>
                        </div>
                        <div className="aid-qa-body">
                          <div className="aid-question-text">{question?.question || answer.question}</div>
                          <div className="aid-ans-label">Candidate's Answer</div>
                          <div className="aid-ans-text">
                            {answer.answer || <span style={{ color: '#CBD5E1', fontStyle: 'italic' }}>No answer provided</span>}
                          </div>
                          {answer.evaluation?.matchedKeywords?.length > 0 && (
                            <>
                              <div className="aid-ans-label" style={{ marginTop: '12px' }}>Matched Keywords</div>
                              <div className="aid-kw-wrap">
                                {answer.evaluation.matchedKeywords.map((kw, i) => (
                                  <span key={i} className="aid-kw-tag">{kw}</span>
                                ))}
                              </div>
                            </>
                          )}
                          {answer.audioRecording && (
                            <div style={{ marginTop: '12px' }}>
                              <div className="aid-ans-label">Voice Recording</div>
                              <audio controls src={answer.audioRecording} style={{ width: '100%' }} />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="aid-mfooter">
                <button className="aid-btn-ghost" onClick={() => setSelectedSubmission(null)}>Close</button>
                <button className="aid-btn-primary" onClick={() => setSelectedSubmission(null)}>Done</button>
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );
};

export default AIInterviewDashboard;